import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  ApiResponse,
  PublicApiScope,
} from '@super/shared-types'
import type {
  ApiKeyListItem,
  CreateApiKeyResponse,
  CreateApiKeyRequest,
  GrantableScopesResponse,
} from './api-keys.client'

export interface AdminUpdateApiKeyRequest {
  name?: string
  scopes?: PublicApiScope[]
}

export class AdminApiKeysClient extends BaseApiClient {
  /**
   * Create a new API key for a specific company (Admin only)
   * Returns the full key (only time it's visible)
   */
  async createApiKey(
    companyId: string,
    request: CreateApiKeyRequest,
  ): Promise<CreateApiKeyResponse> {
    const response = await this.request<ApiResponse<CreateApiKeyResponse>>(
      API_ROUTES.ADMIN.API_KEYS.CREATE,
      {
        method: 'POST',
        body: JSON.stringify({
          ...request,
          companyId,
        }),
      },
    )
    return response.data!
  }

  /**
   * List all API keys for a specific company (Admin only)
   * Key values are not included
   */
  async listApiKeys(companyId: string): Promise<ApiKeyListItem[]> {
    const url = `${API_ROUTES.ADMIN.API_KEYS.LIST}?companyId=${encodeURIComponent(companyId)}`
    const response = await this.request<ApiResponse<ApiKeyListItem[]>>(url, {
      method: 'GET',
    })
    return response.data ?? []
  }

  /**
   * Update an API key's name or scopes (Admin only) — applies immediately
   */
  async updateApiKey(
    id: string,
    companyId: string,
    request: AdminUpdateApiKeyRequest,
  ): Promise<ApiKeyListItem> {
    const url = `${interpolateRoute(API_ROUTES.ADMIN.API_KEYS.UPDATE, { id })}?companyId=${encodeURIComponent(companyId)}`
    const response = await this.request<ApiResponse<ApiKeyListItem>>(url, {
      method: 'PATCH',
      body: JSON.stringify(request),
    })
    return response.data!
  }

  /**
   * Delete an API key (Admin only)
   */
  async deleteApiKey(id: string, companyId: string): Promise<void> {
    const url = `${interpolateRoute(API_ROUTES.ADMIN.API_KEYS.DELETE, { id })}?companyId=${encodeURIComponent(companyId)}`
    await this.request(url, {
      method: 'DELETE',
    })
  }

  /**
   * Scopes the specified company may grant to a new or existing key right
   * now. Same shape as the customer endpoint — drives the create-key UI
   * when a super-admin is viewing on behalf of a company.
   */
  async getGrantableScopes(companyId: string): Promise<PublicApiScope[]> {
    const url = `${API_ROUTES.ADMIN.API_KEYS.GRANTABLE_SCOPES}?companyId=${encodeURIComponent(companyId)}`
    const response = await this.request<ApiResponse<GrantableScopesResponse>>(
      url,
      { method: 'GET' },
    )
    return response.data?.scopes ?? []
  }
}

// Create and export singleton instance
export const adminApiKeysClient = new AdminApiKeysClient()
